/**
 * PATH: packages/ui/src/components/notification-center/NotificationPanel.tsx
 * FILE_ID: UI-NOTIFCENTER-PANEL-001
 * PURPOSE: Header, filters, list, and footer shared by both panel modes.
 * WHY: Slate and blue fills made the panel read as a different theme.
 */

import { AnimatePresence } from 'framer-motion'
import { Bell, X, Settings, CheckCheck, Trash2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { NotificationItem, FilterType } from './types'
import { groupByDate, filterNotifications } from './types'
import { NotificationRow } from './NotificationRow'

const iconButton = 'p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground'

interface NotificationPanelProps {
  notifications: NotificationItem[]
  unreadCount: number
  isLoading: boolean
  filter: FilterType
  onFilterChange: (filter: FilterType) => void
  onMarkAsRead?: (id: string) => void
  onMarkAllAsRead?: () => void
  onDismiss?: (id: string) => void
  onDismissAll?: () => void
  onOpenSettings?: () => void
  onClose?: () => void
  onNavigate?: (url: string) => void
  /** Whether to show the close button in the header */
  showCloseButton?: boolean
  /** Max height class for the notification list */
  listMaxHeight?: string
}

const FILTER_OPTIONS: { id: FilterType; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'mentions', label: 'Mentions' },
]

const FOCUS_OPTIONS: { id: FilterType; label: string }[] = [
  { id: 'focus', label: 'Focus' },
  { id: 'timesheets', label: 'Timesheets' },
  { id: 'qa', label: 'QA' },
  { id: 'updates', label: 'Updates' },
]

export function NotificationPanel({
  notifications,
  unreadCount,
  isLoading,
  filter,
  onFilterChange,
  onMarkAsRead,
  onMarkAllAsRead,
  onDismiss,
  onDismissAll,
  onOpenSettings,
  onClose,
  onNavigate,
  showCloseButton = false,
  listMaxHeight = 'max-h-[450px]',
}: NotificationPanelProps) {
  const filteredNotifications = filterNotifications(notifications, filter)
  const grouped = groupByDate(filteredNotifications)

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-foreground">Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-primary/15 text-primary rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {onMarkAllAsRead && unreadCount > 0 && (
            <button
              onClick={onMarkAllAsRead}
              className={iconButton}
              title="Mark all as read"
            >
              <CheckCheck className="w-4 h-4" />
            </button>
          )}
          {onOpenSettings && (
            <button
              onClick={onOpenSettings}
              className={iconButton}
              title="Notification settings"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
          {showCloseButton && onClose && (
            <button
              onClick={onClose}
              className={iconButton}
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-col gap-1 border-b border-border bg-muted/40 px-4 py-2">
        <div className="flex flex-wrap items-center gap-1">
          {FILTER_OPTIONS.map((option) => (
            <FilterChip
              key={option.id}
              label={option.label}
              count={filterNotifications(notifications, option.id).length}
              active={filter === option.id}
              onClick={() => onFilterChange(option.id)}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {FOCUS_OPTIONS.map((option) => (
            <FilterChip
              key={option.id}
              label={option.label}
              count={filterNotifications(notifications, option.id).length}
              active={filter === option.id}
              onClick={() => onFilterChange(option.id)}
            />
          ))}
        </div>
      </div>

      {/* Notification List */}
      <div className={cn('overflow-y-auto p-3 space-y-4', listMaxHeight)}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Bell className="w-12 h-12 text-muted-foreground mb-3" />
            <p className="text-muted-foreground font-medium">
              {filter === 'all' ? 'No notifications' : `No ${filter} notifications`}
            </p>
            <p className="text-xs text-muted-foreground mt-1">You're all caught up!</p>
          </div>
        ) : (
          <>
            {grouped.map((group) => (
              <NotificationGroupSection key={group.label} label={group.label} items={group.items} onMarkAsRead={onMarkAsRead} onDismiss={onDismiss} onNavigate={onNavigate} />
            ))}
          </>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && onDismissAll && (
        <div className="flex items-center justify-end px-4 py-2 border-t border-border bg-muted/40">
          <button
            onClick={onDismissAll}
            className="text-xs text-muted-foreground hover:text-destructive font-medium flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" />
            Clear all
          </button>
        </div>
      )}
    </>
  )
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors',
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {label}
      <span className={cn('rounded px-1 text-[10px]', active ? 'bg-primary-foreground/20' : 'bg-muted')}>
        {count}
      </span>
    </button>
  )
}

function NotificationGroupSection({
  label,
  items,
  onMarkAsRead,
  onDismiss,
  onNavigate,
}: {
  label: string
  items: NotificationItem[]
  onMarkAsRead?: (id: string) => void
  onDismiss?: (id: string) => void
  onNavigate?: (url: string) => void
}) {
  if (items.length === 0) return null

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <div className="flex-1 h-px bg-border" />
      </div>
      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {items.map((n) => (
            <NotificationRow key={n.id} notification={n} onMarkAsRead={onMarkAsRead} onDismiss={onDismiss} onNavigate={onNavigate} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  )
}
